import { AlertCircle, DollarSign, TrendingUp } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../config/api'

interface BudgetData {
  budget_flz: number
  spent_flz: number
  remaining_flz: number
  spent_percentage: number
  total_donated: number
  recent_donations: Array<{
    amount_flz: number
    created_at: string
    display_name: string | null
  }>
  cost_breakdown: Array<{
    work_type: string
    count: number
    total_cost: number
  }>
}

export default function ResearchBudgetTracker({ researchId }: { researchId: number }) {
  const [budget, setBudget] = useState<BudgetData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/projects/${researchId}/budget`)
        if (res.ok) {
          const data = await res.json() as BudgetData
          setBudget(data)
        }
      } catch {
        // silent
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [researchId])

  if (loading) {return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>}
  if (!budget) {return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>No budget data</div>}

  const isLow = budget.remaining_flz < budget.budget_flz * 0.15
  const barColor = isLow ? '#ef4444' : budget.spent_percentage > 70 ? '#f59e0b' : '#10b981'

  return (
    <div style={{ padding: '20px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
        <DollarSign size={18} style={{ color: 'var(--primary-color)' }} />
        <span style={{ fontWeight: 600, fontSize: 16 }}>Budget Tracker</span>
      </div>

      {/* Overview cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Budget', value: budget.budget_flz, color: '#6366f1' },
          { label: 'Spent', value: budget.spent_flz, color: '#f59e0b' },
          { label: 'Remaining', value: budget.remaining_flz, color: barColor },
        ].map(s => (
          <div key={s.label} style={{
            background: 'var(--card-bg)', border: '1px solid var(--border-color)',
            borderRadius: 12, padding: '16px 18px'
          }}>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {s.label}
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>
              {s.value.toLocaleString()} <span style={{ fontSize: 12, fontWeight: 400 }}>FLZ</span>
            </div>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div style={{
        background: 'var(--card-bg)', border: '1px solid var(--border-color)',
        borderRadius: 12, padding: '18px 20px', marginBottom: 20
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 13 }}>
          <span style={{ color: 'var(--text-secondary)' }}>Budget used</span>
          <span style={{ fontWeight: 600, color: barColor }}>{budget.spent_percentage.toFixed(1)}%</span>
        </div>
        <div style={{ height: 10, background: 'var(--border-color)', borderRadius: 6, overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${budget.spent_percentage}%`,
            background: barColor, borderRadius: 6, transition: 'width 0.4s'
          }} />
        </div>
        {isLow && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            marginTop: 10, fontSize: 12, color: '#ef4444'
          }}>
            <AlertCircle size={13} /> Low budget — consider donating to keep research running
          </div>
        )}
      </div>

      {/* Donations */}
      {budget.total_donated > 0 && (
        <div style={{
          background: 'var(--card-bg)', border: '1px solid var(--border-color)',
          borderRadius: 12, padding: '18px 20px', marginBottom: 20
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <TrendingUp size={15} style={{ color: '#10b981' }} />
            <span style={{ fontWeight: 600, fontSize: 14 }}>
              Donations — {budget.total_donated.toLocaleString()} FLZ total
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {budget.recent_donations.map((d, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', background: 'var(--body-bg)', borderRadius: 8
              }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{d.display_name || 'Anonymous'}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 8 }}>
                    {new Date(d.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#10b981' }}>
                  +{d.amount_flz.toLocaleString()} FLZ
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cost breakdown */}
      {budget.cost_breakdown.length > 0 && (
        <div style={{
          background: 'var(--card-bg)', border: '1px solid var(--border-color)',
          borderRadius: 12, padding: '18px 20px'
        }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14 }}>Cost Breakdown</div>
          {budget.cost_breakdown.map(r => (
            <div key={r.work_type} style={{
              display: 'flex', justifyContent: 'space-between',
              padding: '6px 0', borderBottom: '1px solid var(--border-color)', fontSize: 13
            }}>
              <span style={{ textTransform: 'capitalize', color: 'var(--text-primary)' }}>
                {r.work_type} ({r.count})
              </span>
              <span style={{ fontWeight: 600 }}>{r.total_cost.toLocaleString()} FLZ</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
